import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { Input, Button, Form, Dropdown, Select, Switch } from "antd";
import type { MenuProps } from "antd";

import { Properties, Organizations, Users_Set_Input } from "graphql/generated/graphql";
import { useGetUserByIdQuery } from "graphql/queries/get-user-by-id.generated";
import { useGetPropertiesByOrganizationIdQuery } from "graphql/queries/get-properties-by-organization-id.generated";
import { useGetOrganizationsByWorkspaceIdQuery } from "graphql/queries/get-organizations-by-workspace-id.generated";
import { useUpdateUserByIdMutation } from "graphql/mutations/update-user-by-id.generated";
import { useDeleteUserPropertiesMutation } from "graphql/mutations/delete-user-properties.generated";
import { useAddUserPropertiesMutation } from "graphql/mutations/add-user-properties.generated";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import routeNames from "constants/route-names";
import regex from "constants/regex";
import { UserRoleValue, UserRoleLabel, OrganizationType, isAuthorityRole } from "constants/field-values";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import { compareEntities } from "utils/helpers";
import { canRoleEdit } from "utils/permissions-management";

const UserEdit: React.FC = () => {
	const params = useParams();
	const navigate = useNavigate();
	const { loggedUserValue, updateLoggedUser } = useLoggedUser();
	const [firstName, setFirstName] = useState<string>("");
	const [lastName, setLastName] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [phoneNumber, setPhoneNumber] = useState<string>("");
	const [title, setTitle] = useState<string>("");
	const [role, setRole] = useState<UserRoleValue | string>("");

	const [isAdmin, setIsAdmin] = useState<boolean>(false);
	const [selectedPropertiesKeys, setSelectedPropertiesKeys] = useState<Array<string>>([]);
	const [selectedProperties, setSelectedProperties] = useState<Properties[]>([]);
	const [propertiesDropdownOpen, setPropertiesDropdownOpen] = useState<boolean>(false);
	const [selectedOrganizationsKeys, setSelectedOrganizationsKeys] = useState<Array<string>>([]);
	const [selectedOrganizations, setSelectedOrganizations] = useState<Organizations[]>([]);
	const [organizationsDropdownOpen, setOrganizationsDropdownOpen] = useState<boolean>(false);

	const [updateUserCompleted, setUpdateUserCompleted] = useState<boolean>(false);
	const [addUserPropertiesCompleted, setAddUserPropertiesCompleted] = useState<boolean>(false);
	const [deleteUserPropertiesCompleted, setDeleteUserPropertiesCompleted] = useState<boolean>(false);
	const [userData, setUserData] = useState<any>(null);

	useEffect(() => {
		if (userData) {
			if (
				loggedUserValue.organization_id !== userData.organization_id ||
				!canRoleEdit(loggedUserValue.role as UserRoleValue, userData.role as UserRoleValue)
			) {
				navigate(routeNames.ADMIN_USERS);
			}
		}
	}, [loggedUserValue, userData, navigate]);

	const handleOnChangePhoneNumber = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { value: inputValue } = event.target;

		if (regex.phoneWithNumbers.test(inputValue) || regex.phoneWithNumbersAndDashes.test(inputValue)) {
			setPhoneNumber(inputValue);
		}
	}, []);

	const handleOnCompleteGetPropertiesByOrganizationId = useCallback(data => {
		// console.log("Complete Get Properties By OrganizationId data: ", data);
	}, []);

	const handleOnErrorGetPropertiesByOrganizationId = useCallback((error: ApolloError) => {
		console.log("Error Get Properties By OrganizationId: ", error);
	}, []);

	const { data: propertiesByOrganizationData, loading: propertiesByOrganizationLoading } =
		useGetPropertiesByOrganizationIdQuery({
			skip: !loggedUserValue.organization_id,
			variables: {
				organization_id: loggedUserValue.organization_id
			},
			errorPolicy: "none",
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetPropertiesByOrganizationId,
			onCompleted: handleOnCompleteGetPropertiesByOrganizationId
		});

	const handleOnCompleteGetOrganizationsByWorkspaceId = useCallback(data => {
		// console.log("Complete Get Organizations By WorkspaceId data: ", data);
	}, []);

	const handleOnErrorGetOrganizationsByWorkspaceId = useCallback((error: ApolloError) => {
		console.log("Error Get Organizations By WorkspaceId Error: ", error);
	}, []);

	const { data: organizationsByWorkspaceData, loading: organizationsByWorkspaceLoading } =
		useGetOrganizationsByWorkspaceIdQuery({
			skip: !loggedUserValue.workspace_id,
			variables: {
				workspace_id: loggedUserValue.workspace_id,
				org_types: [OrganizationType.Customer, OrganizationType.Vendor, OrganizationType.Authority]
			},
			errorPolicy: "none",
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetOrganizationsByWorkspaceId,
			onCompleted: handleOnCompleteGetOrganizationsByWorkspaceId
		});

	const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
		console.log("Error Get Users By Id Error: ", error);
	}, []);

	const handleOnCompletedGetUsers = useCallback(data => {
		const users_by_pk = data.users_by_pk[0];

		if (users_by_pk) {
			const { user_properties, organization, first_name, last_name, email, role, phone_number, title, is_admin } =
				users_by_pk;

			setFirstName(first_name);
			setLastName(last_name);
			setEmail(email);
			setRole(role);
			setPhoneNumber(phone_number);
			setTitle(title);
			setIsAdmin(is_admin);
			setUserData(users_by_pk);

			const properties: Properties[] = [];
			const propertiesIds: string[] = [];

			user_properties.forEach(element => {
				properties.push(element.property);
				propertiesIds.push(element.property_id);
			});

			setSelectedProperties(properties);
			setSelectedPropertiesKeys(propertiesIds);

			if (organization) {
				setSelectedOrganizations([organization]);
				setSelectedOrganizationsKeys([organization.id]);
			}
		}
	}, []);

	const { loading } = useGetUserByIdQuery({
		skip: !params.userId,
		variables: {
			id: params.userId || "",
			is_not_deleted: true
		},
		errorPolicy: "all",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorsGetUsers,
		onCompleted: handleOnCompletedGetUsers
	});

	const handleOnSelectProperties = useCallback(
		args => {
			const { selectedKeys } = args;
			const { properties } = propertiesByOrganizationData || {};

			if (selectedPropertiesKeys.includes("property-dropdown-option-all")) {
				if (selectedKeys.includes("property-dropdown-option-all")) {
					// select all is selected but the user unselect an option
					if (properties && properties.length + 1 !== selectedKeys.length) {
						const newSelectedProperties = properties?.filter(element => selectedKeys.includes(element.id));

						const selectedKeysCopy = selectedKeys.filter(element => element !== "property-dropdown-option-all");

						setSelectedProperties(newSelectedProperties as Array<never>);
						setSelectedPropertiesKeys(selectedKeysCopy);
						return;
					}
				} else {
					// select all is unselected
					setSelectedProperties([]);
					setSelectedPropertiesKeys([]);
					return;
				}
			} else {
				// select all is selected
				if (selectedKeys.includes("property-dropdown-option-all") && properties && properties.length) {
					const propertiesKeys = properties?.map(element => element.id) || [];
					const newPropertiesKeys = ["property-dropdown-option-all"].concat(propertiesKeys);

					setSelectedProperties(properties as Properties[]);
					setSelectedPropertiesKeys(newPropertiesKeys);
					return;
				}
			}

			const newSelectedProperties = properties?.filter(element => selectedKeys.includes(element.id));

			setSelectedProperties(newSelectedProperties as Array<never>);
			setSelectedPropertiesKeys(selectedKeys);
		},
		[propertiesByOrganizationData, selectedPropertiesKeys]
	);

	const handleOnSelectOrganization = useCallback(
		args => {
			const { selectedKeys } = args;
			const { organizations } = organizationsByWorkspaceData || {};

			if (selectedKeys[0] === selectedOrganizationsKeys[0]) {
				setSelectedOrganizations([]);
				setSelectedOrganizationsKeys([]);
			} else {
				const newSelectedOrganizations = organizations?.filter(element => selectedKeys.includes(element.id));

				setSelectedOrganizations(newSelectedOrganizations as Array<never>);
				setSelectedOrganizationsKeys(selectedKeys);
			}
		},
		[organizationsByWorkspaceData, selectedOrganizationsKeys]
	);

	const handleOnRemoveProperty = useCallback(
		propertyId => {
			const newSelectedProperties = selectedProperties?.filter(element => element.id !== propertyId);

			const newSelectedPropertiesKeys = selectedPropertiesKeys?.filter(element => element !== propertyId);

			setSelectedProperties(newSelectedProperties as Array<never>);
			setSelectedPropertiesKeys(newSelectedPropertiesKeys);
		},
		[selectedProperties, selectedPropertiesKeys]
	);

	const handleOnCompleteUpdateUser = useCallback(
		(data: any) => {
			setUpdateUserCompleted(true);

			// console.log("Complete Update User data: ", data);
			const { update_users_by_pk } = data || {};
			const { id } = update_users_by_pk || {};
			if (update_users_by_pk && id === loggedUserValue.id) {
				updateLoggedUser(update_users_by_pk);
			}
		},
		[loggedUserValue.id]
	);

	const handleOnErrorUpdateUser = useCallback((error: ApolloError) => {
		console.log("Update Organization Error: ", error);
		setUpdateUserCompleted(true);
	}, []);

	const [updateUserByIdMutation, { loading: updateUserByIdLoading }] = useUpdateUserByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUpdateUser,
		onCompleted: handleOnCompleteUpdateUser
	});

	const handleOnCompleteAddUserProperties = useCallback((data: any) => {
		//console.log("Complete Add User Properties data: ", data);
		setAddUserPropertiesCompleted(true);
	}, []);

	const handleOnErrorAddUserProperties = useCallback((error: ApolloError) => {
		console.log("Add User Properties Error: ", error);
		setAddUserPropertiesCompleted(true);
	}, []);

	const [addUserPropertiesMutation, { loading: addUserPropertiesLoading }] = useAddUserPropertiesMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddUserProperties,
		onCompleted: handleOnCompleteAddUserProperties
	});

	const handleOnCompleteDeleteUserProperties = useCallback((data: any) => {
		//console.log("Complete Delete User Properties data: ", data);
		setDeleteUserPropertiesCompleted(true);
	}, []);

	const handleOnErrorDeleteUserProperties = useCallback((error: ApolloError) => {
		console.log("Delete User Properties Error: ", error);
		setDeleteUserPropertiesCompleted(true);
	}, []);

	const [deleteUserPropertiesMutation, { loading: deleteUserPropertiesLoading }] = useDeleteUserPropertiesMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorDeleteUserProperties,
		onCompleted: handleOnCompleteDeleteUserProperties
	});

	const handleUpdateProperties = useCallback(async () => {
		const { user_properties = [] } = userData?.users_by_pk[0] || {};

		if (user_properties.length) {
			const currentProperties = user_properties.map(userProp => userProp.property);

			const comparison = compareEntities(currentProperties, selectedProperties);

			if (comparison.toRemove.length) {
				try {
					const propertiesIdsToRemove = comparison.toRemove.map(property => property.id);

					await deleteUserPropertiesMutation({
						variables: {
							where: {
								user_id: { _eq: params.userId },
								property_id: { _in: propertiesIdsToRemove }
							}
						}
					});
				} catch (error) {
					console.log("Error on delete users properties. Error: ", error);
				}
			} else {
				setDeleteUserPropertiesCompleted(true);
			}

			if (comparison.toAdd.length) {
				try {
					const propertiesToAdd = comparison.toAdd.map(property => {
						return {
							property_id: property.id,
							user_id: params.userId
						};
					});

					await addUserPropertiesMutation({
						variables: {
							objects: propertiesToAdd
						}
					});
				} catch (error) {
					console.log("Error on add users properties. Error: ", error);
				}
			} else {
				setAddUserPropertiesCompleted(true);
			}
		} else {
			setDeleteUserPropertiesCompleted(true);
			if (selectedProperties.length) {
				try {
					const propertiesToAdd = selectedProperties.map(property => {
						return {
							property_id: property.id,
							user_id: params.userId
						};
					});

					await addUserPropertiesMutation({
						variables: {
							objects: propertiesToAdd
						}
					});
				} catch (error) {
					console.log("Error on add users properties. Error: ", error);
				}
			} else {
				setAddUserPropertiesCompleted(true);
			}
		}
	}, [params.userId, selectedProperties, userData, addUserPropertiesMutation, deleteUserPropertiesMutation]);

	const handleOnSubmit = useCallback(async () => {
		if (params.userId) {
			try {
				let userInput: Users_Set_Input = {
					first_name: firstName,
					last_name: lastName,
					phone_number: phoneNumber,
					email: email,
					title: title,
					is_admin: isAdmin,
					role: role
				};

				if (loggedUserValue.role === UserRoleValue.admin_user) {
					if (selectedOrganizationsKeys.length) {
						userInput.organization_id = selectedOrganizationsKeys[0];
					} else {
						userInput.organization_id = null;
					}
				}

				await updateUserByIdMutation({
					variables: {
						set: userInput,
						id: params.userId || ""
					}
				});
			} catch (error) {
				console.log("Error on submit users update. Error: ", error);
			}

			try {
				await handleUpdateProperties();
			} catch (error) {
				console.log("Error on submit users properties update. Error: ", error);
			}
		}
	}, [
		handleUpdateProperties,
		updateUserByIdMutation,
		params.userId,
		firstName,
		lastName,
		phoneNumber,
		email,
		title,
		role,
		isAdmin,
		selectedOrganizationsKeys
	]);

	const propertiesOptions = useMemo(() => {
		const { properties } = propertiesByOrganizationData || {};

		const isAllSelected = selectedPropertiesKeys.includes("property-dropdown-option-all");

		if (properties && properties.length) {
			const organizationProperties: MenuProps["items"] = properties.reduce(
				(options, property, currentindex, array) => {
					const optionId = `property-dropdown-option-${currentindex + 1}-${property?.id}`;

					const isSelected = selectedPropertiesKeys.includes(property?.id as string);

					const newOption = {
						key: `${property?.id}`,
						icon: (
							<div>
								<input
									className="custom-checkbox"
									id={optionId}
									type="checkbox"
									onClick={e => e.stopPropagation()}
									checked={isSelected}
									onChange={() => undefined}
								/>
								<label htmlFor={optionId} className="custom-checkbox-label">
									{property?.name}
								</label>
							</div>
						)
					};

					options.push(newOption);

					return options;
				},
				[
					{
						key: "property-dropdown-option-all",
						icon: (
							<div>
								<input
									className="custom-checkbox"
									id="property-dropdown-option-all"
									type="checkbox"
									checked={isAllSelected}
									onChange={() => undefined}
									onClick={e => e.stopPropagation()}
								/>
								<label htmlFor="property-dropdown-option-all" className="custom-checkbox-label">
									All properties
								</label>
							</div>
						)
					}
				]
			);

			return organizationProperties;
		}

		return [];
	}, [propertiesByOrganizationData, selectedPropertiesKeys]);

	const organizationsOptions = useMemo(() => {
		const { organizations } = organizationsByWorkspaceData || {};

		if (organizations && organizations.length) {
			const organizationProperties: MenuProps["items"] = organizations.map((organization, currentindex, array) => {
				const optionId = `organization-dropdown-option-${currentindex + 1}-${organization?.id}`;

				const isSelected = selectedOrganizationsKeys.includes(organization?.id as string);

				const newOption = {
					key: `${organization?.id}`,
					icon: (
						<div>
							<input
								className="custom-checkbox"
								id={optionId}
								type="checkbox"
								onClick={e => e.stopPropagation()}
								checked={isSelected}
								onChange={() => undefined}
							/>
							<label htmlFor={optionId} className="custom-checkbox-label">
								{organization?.name}
							</label>
						</div>
					)
				};

				return newOption;
			});

			return organizationProperties;
		}

		return [];
	}, [organizationsByWorkspaceData, selectedOrganizationsKeys]);

	const availableRolesValues = useMemo(() => {
		let availableRoles: UserRoleValue[] = [];

		switch (loggedUserValue.role) {
			case UserRoleValue.admin_user:
				availableRoles = Object.values(UserRoleValue);
				break;
			case UserRoleValue.customer_manager:
				availableRoles.push(UserRoleValue.customer_manager, UserRoleValue.customer_employee);
				break;
			case UserRoleValue.vendor_manager:
				availableRoles.push(
					UserRoleValue.vendor_manager,
					UserRoleValue.vendor_employee,
					UserRoleValue.customer_manager
				);
				break;
			case UserRoleValue.authority_manager:
				availableRoles.push(UserRoleValue.authority_manager, UserRoleValue.authority_user);
				break;
			default:
				break;
		}

		return availableRoles;
	}, [loggedUserValue.role]);

	useEffect(() => {
		if (updateUserCompleted && addUserPropertiesCompleted && deleteUserPropertiesCompleted && params.userId) {
			const path = `${routeNames.ADMIN_USER_DETAILS.replace(":userId", params.userId)}`;

			navigate(path);
		}
	}, [updateUserCompleted, addUserPropertiesCompleted, deleteUserPropertiesCompleted]);

	const isLoading =
		loading ||
		propertiesByOrganizationLoading ||
		updateUserByIdLoading ||
		deleteUserPropertiesLoading ||
		organizationsByWorkspaceLoading ||
		addUserPropertiesLoading;

	const handleOnRemoveOrganization = useCallback(() => {
		if (!isLoading) {
			setSelectedOrganizations([]);
			setSelectedOrganizationsKeys([]);
		}
	}, [isLoading]);

	return (
		<div className="add-product-screen-container">
			<span className="add-product-page-title">{"Edit User"}</span>
			<Form className="add-product-form" autoComplete="off">
				<span className="add-product-form-title">{"User Details"}</span>
				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please input the First name." }]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="firstName"
						placeholder="First Name"
						className="add-product-input-pair"
						value={firstName}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setFirstName(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please input the Last name." }]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="lastName"
						placeholder="Last Name"
						className="add-product-input-pair"
						value={lastName}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setLastName(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please input the email." }]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="email"
						placeholder="Email"
						className="add-product-input-pair"
						value={email}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setEmail(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				{availableRolesValues.length ? (
					<Form.Item
						className="login-form-item"
						rules={[{ required: true, message: "Please select the user role." }]}
						required
						validateTrigger="onBlur"
					>
						<Select
							value={role || undefined}
							className="add-product-input-select"
							onChange={value => setRole(value)}
							options={availableRolesValues.map(element => {
								return { value: element, label: UserRoleLabel[element] };
							})}
							placeholder="User Role"
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				) : null}

				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please input the phone number." }]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="phoneNumber"
						placeholder="Phone"
						className="add-product-input-pair"
						value={phoneNumber}
						autoComplete="off"
						onChange={handleOnChangePhoneNumber}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please input the user title." }]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="title"
						placeholder="Title"
						className="add-product-input-pair"
						value={title}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setTitle(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				{loggedUserValue.role === UserRoleValue.admin_user ? (
					<div className="organization-button-container">
						<span className="switch-label">Admin</span>
						<Switch checked={isAdmin} onChange={checked => setIsAdmin(checked)} disabled={isLoading} />
					</div>
				) : null}
			</Form>

			{/* An authority user's properties are derived from their organization's
			    jurisdiction, so there is nothing to grant here — and saving the section
			    would delete the rows the backend granted. */}
			{!isAuthorityRole(role) ? (
				<div className="add-product-form">
					<span className="add-product-form-title">{"Property Access"}</span>

					<Dropdown
						menu={{
							items: propertiesOptions,
							selectable: true,
							multiple: true,
							onSelect: args => handleOnSelectProperties(args),
							onDeselect: args => handleOnSelectProperties(args),
							selectedKeys: selectedPropertiesKeys
						}}
						disabled={isLoading}
						arrow
						className="organization-properties-dropdown"
						overlayClassName="products-dropdown-overlay"
						trigger={["click"]}
						open={propertiesDropdownOpen}
						onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
							if (info.source === "trigger" && open === false) {
								setPropertiesDropdownOpen(false);
							}
						}}
					>
						<Button
							onClick={() => {
								setPropertiesDropdownOpen(previousValue => !previousValue);
							}}
							disabled={isLoading}
						>
							Select Properties
							<img
								src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
								className="chevron-button"
								title={"Select Properties"}
							/>
						</Button>
					</Dropdown>

					<div className="organization-selected-properties-container">
						{selectedProperties.map((element: Properties) => {
							if (element) {
								return (
									<div key={element.id} className="organization-selected-property">
										<span className="organization-selected-property-name">{element.name}</span>
										<span
											className="organization-selected-property-delete"
											onClick={() => {
												if (!isLoading) {
													handleOnRemoveProperty(element.id);
												}
											}}
										>
											{"X"}
										</span>
									</div>
								);
							} else {
								return null;
							}
						})}
					</div>
				</div>
			) : null}

			{loggedUserValue.role === UserRoleValue.admin_user ? (
				<div className="add-product-form">
					<span className="page-title">{"Organization Access"}</span>

					<Dropdown
						menu={{
							items: organizationsOptions,
							selectable: true,
							onSelect: args => handleOnSelectOrganization(args),
							onDeselect: args => handleOnSelectOrganization(args),
							selectedKeys: selectedOrganizationsKeys
						}}
						arrow
						className="organization-properties-dropdown"
						overlayClassName="products-dropdown-overlay"
						trigger={["click"]}
						disabled={isLoading}
						open={organizationsDropdownOpen}
						onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
							if (info.source === "trigger" && open === false) {
								setOrganizationsDropdownOpen(false);
							}
						}}
					>
						<Button
							onClick={() => {
								setOrganizationsDropdownOpen(previousValue => !previousValue);
							}}
							disabled={isLoading}
						>
							Select Organizations
							<img
								src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
								className="chevron-button"
								title={"Select Organization"}
							/>
						</Button>
					</Dropdown>

					<div className="organization-selected-properties-container">
						{selectedOrganizations.map((organization: Organizations) => {
							return (
								<div key={organization.id} className="organization-selected-property">
									<span className="organization-selected-property-name">{organization.name}</span>
									<span className="organization-selected-property-delete" onClick={handleOnRemoveOrganization}>
										{"X"}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			) : null}

			<div className="add-product-button-container">
				<Button className="admin-add-product-button" htmlType="submit" onClick={handleOnSubmit} disabled={isLoading}>
					Save
				</Button>
			</div>
			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default UserEdit;
